// Closing a modal by clicking its backdrop — without closing it when someone
// selects text and releases the mouse outside the box.
//
// Every dialog in the app spelled this as `<div className="task-modal-overlay"
// onClick={onCancel}>`, and a `click` fires on the nearest ancestor COMMON to
// where the button went down and where it came up. So drag-selecting the contents
// of a field and overshooting the dialog's edge — which is what selecting the
// whole of a short input looks like — dismissed the dialog and threw the work
// away. QA hit it in the Insert-link dialog (ENG-53866); the same gesture lost a
// half-typed page title in the new-page dialog and a task description in the
// create-task modal, because all ten overlays shared the pattern.
//
// The fix is to require the press to have STARTED on the backdrop. A click whose
// mousedown landed inside the dialog is a gesture that began inside it, whatever
// the mouse did afterwards.
//
// A plain function rather than a hook, deliberately: it spreads onto the existing
// overlay div as two props, so adopting it is a one-line change at each of the ten
// call sites instead of restructuring their JSX. The gesture state is module-scope
// because a pointer can only be in one drag at a time — there is no second
// concurrent gesture for two dialogs to confuse, and modals don't stack here.
//
// `target === currentTarget` on both halves keeps this to the backdrop itself
// rather than anything drawn on top of it, so a dialog no longer depends on its own
// `onClick={(e) => e.stopPropagation()}` to defend itself.

let startedOnOverlay = false;

export interface OverlayDismissProps {
  onMouseDown: (e: React.MouseEvent) => void;
  onClick: (e: React.MouseEvent) => void;
}

export function overlayDismiss(onDismiss: () => void): OverlayDismissProps {
  return {
    onMouseDown: (e) => { startedOnOverlay = e.target === e.currentTarget; },
    onClick: (e) => {
      const dismiss = e.target === e.currentTarget && startedOnOverlay;
      startedOnOverlay = false;
      if (dismiss) onDismiss();
    },
  };
}

/** Test seam — a gesture left half-finished by one case must not leak into the next. */
export function resetOverlayGesture(): void {
  startedOnOverlay = false;
}